/**
 * The two or three numbers an appliance card carries beside its name.
 *
 * The card used to print one reading, and for most of the house that reading
 * is the least interesting thing it knows: a computer says "On" while its CPU
 * load, its memory and its temperature sit unread, and an oven says "Running"
 * while its cavity climbs to the setpoint (owner, 25.10.2026: *"our appliance
 * widget cards should show more info.. like temperatures, cpus"*).
 *
 * This is the ranking those extra readings come from — pure, so the card only
 * draws what it is handed:
 *
 *  1. a temperature (a cavity, a CPU, a drive) — the reading people look for;
 *  2. a load (CPU, memory, disk, usage) — the reading an owner looks for;
 *  3. power, battery, then the remaining environment readings.
 *
 * Automation state (`*__*`) and booleans are never facts: one is drawn by the
 * badges, the other is a state the card already speaks.
 */

export interface ApplianceFactRow {
  valueKey?: string | null;
  currentValue?: unknown;
  unit?: string | null;
  name?: string | null;
}

export interface ApplianceFact {
  key: string;
  /** A short prefix when the number alone would be ambiguous ("CPU 34%"). */
  label: string;
  /** The number as the card prints it, unit included. */
  text: string;
  /** The value's own name, for the tooltip. */
  title: string;
  /** 0 = the most interesting class of reading; used for ordering only. */
  rank: number;
}

interface Reading {
  key: string;
  value: number;
  unit: string;
  name: string;
  /** A value the user set rather than one the device reports. */
  target: boolean;
}

const NUMERIC = /^-?\d+(\.\d+)?$/;

function toReading(row: ApplianceFactRow): Reading | null {
  const key = String(row.valueKey ?? '').toLowerCase();
  if (!key) return null;
  // `oven__remaining_time` is an automation's state, drawn by the badge stack.
  if (key.includes('__')) return null;

  const raw = row.currentValue;
  const numeric =
    typeof raw === 'number' ? raw : typeof raw === 'string' && NUMERIC.test(raw.trim()) ? Number(raw) : null;
  if (numeric === null || !Number.isFinite(numeric)) return null;

  return {
    key,
    value: numeric,
    unit: String(row.unit ?? '').trim(),
    name: String(row.name ?? key).trim(),
    target: key.startsWith('target_') || key.endsWith('_setpoint') || key === 'setpoint',
  };
}

/** At most one decimal: a card is not a laboratory. */
function formatNumber(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/** Which class of reading a key belongs to, and how it is labelled. */
function classify(reading: Reading): { rank: number; label: string; fallbackUnit: string } | null {
  const key = reading.key;
  if (/temp|temperature/.test(key)) {
    const label = key.startsWith('cpu') ? 'CPU' : '';
    return { rank: 0, label, fallbackUnit: reading.unit || '°C' };
  }
  if (/cpu|load|memory|disk|usage|occupancy|volume/.test(key)) {
    const label = key.startsWith('cpu')
      ? 'CPU'
      : key.includes('memory') || key.startsWith('mem')
        ? 'Mem'
        : key.includes('disk')
          ? 'Disk'
          : key.includes('volume')
            ? 'Vol'
            : key.includes('occupancy')
              ? 'Usage'
              : 'Load';
    return { rank: 1, label, fallbackUnit: reading.unit || '%' };
  }
  if (/power|watt/.test(key)) return { rank: 2, label: '', fallbackUnit: reading.unit || 'W' };
  if (/battery/.test(key)) return { rank: 3, label: '', fallbackUnit: reading.unit || '%' };
  if (/humidity|pressure|co2|rssi|current|voltage/.test(key)) {
    return { rank: 4, label: '', fallbackUnit: reading.unit };
  }
  return null;
}

/**
 * The readings worth showing beside the primary one, most interesting first.
 *
 * `excludeKeys` are the values the card already prints (its primary reading),
 * so the same number never appears twice; `limit` keeps the row to what fits.
 */
export function applianceFacts(
  rows: readonly ApplianceFactRow[] | null | undefined,
  options: { excludeKeys?: readonly string[]; limit?: number } = {},
): ApplianceFact[] {
  const excluded = new Set((options.excludeKeys ?? []).map((key) => String(key).toLowerCase()));
  const limit = options.limit ?? 2;
  const facts: ApplianceFact[] = [];

  for (const row of rows ?? []) {
    const reading = toReading(row);
    if (!reading || excluded.has(reading.key)) continue;

    const kind = classify(reading);
    if (!kind) continue;

    const unit = reading.unit || kind.fallbackUnit;
    const text = `${reading.target ? '→ ' : ''}${formatNumber(reading.value)}${unit}`;
    facts.push({
      key: reading.key,
      label: reading.target ? '' : kind.label,
      text,
      title: reading.name,
      rank: kind.rank,
    });
  }

  // Stable within a rank: the hub's own order is the device's own order.
  return facts.sort((a, b) => a.rank - b.rank).slice(0, Math.max(0, limit));
}

/** `CPU 34%` — or just the number when its unit already says what it is. */
export function applianceFactText(fact: ApplianceFact): string {
  return fact.label ? `${fact.label} ${fact.text}` : fact.text;
}
